import { formatPesoAmount } from '../utils/money';
import React from 'react';
import { FiInfo } from 'react-icons/fi';

const formatCurrency = (n) => `$${formatPesoAmount(Number(n || 0))}`;

const ValidationInfo = ({ validationError }) => {
  if (!validationError) return null;
  return (
    <div className="group relative inline-flex shrink-0">
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-300"
        aria-label="Ver detalle del error de descuento"
      >
        <FiInfo size={13} />
      </button>
      <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-72 rounded-md border border-red-200 bg-white p-3 text-left text-xs leading-snug text-gray-700 shadow-lg group-hover:block group-focus-within:block">
        <div className="font-semibold text-red-700">
          {validationError.title || 'Revisar línea'}
        </div>
        {validationError.detail && (
          <p className="mt-1">{validationError.detail}</p>
        )}
        {validationError.reason && (
          <p className="mt-2 text-gray-600">{validationError.reason}</p>
        )}
        {validationError.fix && (
          <p className="mt-2 font-medium text-gray-800">{validationError.fix}</p>
        )}
      </div>
    </div>
  );
};

const QuotationSummaryModal = ({
  isOpen,
  onClose,
  client,
  clientType,
  items = [],
  itemsSubtotal = 0,
  orderDiscountAmount = 0,
  orderDiscountPercentage = null,
  totalAmount = 0,
  onSaveDraft,
  onSend,
  onConvert,
  disableActions,
  canSaveDraft = true,
  canSend = true,
  canConvert = true,
  getItemValidationError,
}) => {
  if (!isOpen) return null;
  const effectiveOrderDiscount =
    orderDiscountPercentage && itemsSubtotal > 0
      ? Math.floor(itemsSubtotal * (orderDiscountPercentage / 100))
      : Number(orderDiscountAmount) || 0;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto pt-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="p-4 max-w-3xl w-full mx-4 bg-white rounded-lg shadow-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2">
          <div>
            <h2 className="text-xl font-bold">Resumen de cotización</h2>
            <p className="text-sm text-gray-600">Revisa el detalle antes de enviar o convertir.</p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
          >
            Cerrar
          </button>
        </div>

        <div className="mt-4 space-y-4 overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 border rounded p-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Cliente</h3>
              <p className="text-sm">
                {client?.name || 'Detalle sin cliente'}
                {client?.rut && <span className="text-gray-500"> — {client.rut}</span>}
              </p>
              {client?.email && <p className="text-xs text-gray-600">{client.email}</p>}
            </div>
            <div className="text-sm text-gray-700">
              <h3 className="text-sm font-semibold">Tipo de cliente</h3>
              <p className="uppercase text-xs">{clientType || 'No definido'}</p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">Producto</th>
                  <th className="px-3 py-2 text-left">Unidad</th>
                  <th className="px-3 py-2 text-left">Cant</th>
                  <th className="px-3 py-2 text-left">Precio</th>
                  <th className="px-3 py-2 text-left">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const qty = Number(item.quantity || 0);
                  const explicitSubtotal = Number(item.subtotal);
                  const subtotal = Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0
                    ? Math.floor(explicitSubtotal)
                    : Math.floor(((Number(item.price) || 0) - (Number(item.discount) || 0)) * qty);
                  const unit = qty > 0 ? subtotal / qty : (Number(item.price) || 0) - (Number(item.discount) || 0);
                  const hasPromotion = item.promotionSnapshot?.kind === 'temporary_price' || (item.discountSource === 'promotion' && Number(item.promotionDiscountAmount || 0) > 0);
                  const validationError = typeof getItemValidationError === 'function'
                    ? getItemValidationError(item, { index: idx })
                    : null;
                  return (
                    <tr key={idx} className={`border-t ${validationError ? 'bg-red-50' : ''}`}>
                      <td className="px-3 py-2 font-semibold">
                        <div className="flex min-w-0 items-center gap-1.5">
                          <span className="min-w-0 truncate">{item.varietyName || item.productName}</span>
                          <ValidationInfo validationError={validationError} />
                        </div>
                        {hasPromotion && (
                          <div className="text-xs font-medium text-emerald-700">
                            {item.promotionSnapshot?.kind === 'temporary_price' ? 'Temporal: ' : 'Promo: '}{item.promotionName || 'Promocion'}
                          </div>
                        )}
                        {validationError && (
                          <div className="mt-1 rounded border border-red-200 bg-white px-2 py-1 text-xs font-normal leading-snug text-red-700">
                            {validationError.detail || 'Revisa la configuración de esta línea.'}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{item.metricUnit || 'unidad'}</td>
                      <td className="px-3 py-2 text-gray-700">{item.quantity}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {item.discount > 0 || hasPromotion ? (
                          <span>
                            <span className="line-through mr-1">{formatCurrency(item.price)}</span>
                            <span>{formatCurrency(unit)}</span>
                          </span>
                        ) : (
                          formatCurrency(item.price)
                        )}
                      </td>
                      <td className="px-3 py-2 font-semibold">{formatCurrency(subtotal)}</td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                      No hay productos en la cotización.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border rounded p-3 text-sm space-y-1">
            {effectiveOrderDiscount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Descuento orden</span>
                <span>
                  -{formatCurrency(effectiveOrderDiscount)}
                  {orderDiscountPercentage ? ` (${orderDiscountPercentage}%)` : ''}
                </span>
              </div>
            )}
            <div className="flex justify-between font-bold text-blue-700">
              <span>Total</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-20 mt-4 border-t bg-white pt-3 flex flex-wrap gap-2 justify-end">
          {canSaveDraft && (
            <button
              onClick={onSaveDraft}
              disabled={disableActions}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
            >
              Guardar borrador
            </button>
          )}
          {canSend && (
            <button
              onClick={onSend}
              disabled={disableActions}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
            >
              Enviar
            </button>
          )}
          {canConvert && (
            <button
              onClick={onConvert}
              disabled={disableActions}
              className="px-3 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-60"
            >
              Convertir a venta
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotationSummaryModal;
